import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ExternalLink, ScanLine } from "lucide-react";
import { Container, Section, Eyebrow } from "./Primitives";
import { Reveal } from "../lib/motion";
import { RASHI_NAV } from "../content/nav";

/**
 * Seven-section product content wall.
 *
 * Rendered only when the API returns `product.content_v2`. Every field is
 * required to have been reviewed for voice; nothing here is inferred from
 * catalogue data at render time. The founder reviews section 6 (refuse_list)
 * per product and it should always feel bespoke, not templated.
 *
 * Structure per the brief:
 *   1. STAGE           — fact bar + lede
 *   2. WHAT IT IS      — material_reality
 *   3. THE PLANET      — planet_reading
 *   4. WHY WORN        — worn_as (as attribution)
 *   5. WHO IT SUITS    — who_it_suits
 *   6. WE WILL NOT SAY — refuse_list
 *   7. PROOF           — certificate + rudraksha X-ray + video (from product)
 */
export function ProductContentV2({ product, cv }) {
  const rashiLabels = (cv.planet_reading?.rashis || [])
    .map((s) => RASHI_NAV.find((r) => r.slug === s)?.label || s)
    .join(" · ");

  return (
    <>
      {/* Section 1 — STAGE (fact bar + lede). The title, deva name, price and
          add-to-cart already sit above this on the buy panel. This block
          anchors the item's material identity before any prose. */}
      <Section className="pt-16 pb-4" data-testid="pv2-stage">
        <Container>
          <div className="pv2">
            {cv.lede && (
              <p className="pv2-lede" data-testid="pv2-lede">{cv.lede}</p>
            )}
            {cv.fact_bar?.length > 0 && (
              <div className="pv2-fact-bar mt-9" data-testid="pv2-fact-bar">
                {cv.fact_bar.map((f) => (
                  <div key={f.label}>
                    <div className="pv2-fact-k">{f.label}</div>
                    <div className="pv2-fact-v">{f.value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Container>
      </Section>

      {/* Section 2 — WHAT IT ACTUALLY IS */}
      {cv.material_reality && (
        <Section className="py-14" data-testid="pv2-material">
          <Container>
            <div className="pv2">
              <Reveal>
                <p className="pv2-eyebrow">Section 2</p>
                <h2 className="pv2-h2 mt-2">{cv.material_reality.title}</h2>
              </Reveal>
              <div className="mt-6 space-y-5">
                {cv.material_reality.paras.map((t, i) => (
                  <p key={i} className="pv2-para">{t}</p>
                ))}
              </div>
            </div>
          </Container>
        </Section>
      )}

      {/* Section 3 — THE PLANET IT ANSWERS TO */}
      {cv.planet_reading && (
        <Section className="py-14 border-t border-rule/60" data-testid="pv2-planet">
          <Container>
            <div className="pv2">
              <Reveal>
                <p className="pv2-eyebrow">Section 3 · The planet it answers to</p>
                <h2 className="pv2-h2 mt-2">
                  {cv.planet_reading.graha} — {cv.planet_reading.planet_english}
                </h2>
              </Reveal>
              <div className="mt-5 grid gap-8 sm:grid-cols-[1fr_1fr]">
                <div>
                  <p className="pv2-fact-k">Presiding form</p>
                  <p className="pv2-para mt-1.5 !text-[15px]">{cv.planet_reading.presiding_form}</p>
                </div>
                {rashiLabels && (
                  <div>
                    <p className="pv2-fact-k">Associated rashis</p>
                    <p className="pv2-para mt-1.5 !text-[15px]">{rashiLabels}</p>
                  </div>
                )}
              </div>
              <p className="pv2-para mt-6">{cv.planet_reading.why}</p>
            </div>
          </Container>
        </Section>
      )}

      {/* Section 4 — WHY IT IS WORN (attribution, never effect) */}
      {cv.worn_as && (
        <Section className="py-14 border-t border-rule/60" data-testid="pv2-worn">
          <Container>
            <div className="pv2">
              <Reveal>
                <p className="pv2-eyebrow">Section 4</p>
                <h2 className="pv2-h2 mt-2">{cv.worn_as.title}</h2>
              </Reveal>
              <div className="mt-6 space-y-5">
                {cv.worn_as.paras.map((t, i) => (
                  <p key={i} className="pv2-para">{t}</p>
                ))}
              </div>
            </div>
          </Container>
        </Section>
      )}

      {/* Section 5 — WHO IT SUITS */}
      {cv.who_it_suits && (
        <Section className="py-14 border-t border-rule/60" data-testid="pv2-suits">
          <Container>
            <div className="pv2 pv2-suits">
              <Reveal>
                <p className="pv2-eyebrow">Section 5</p>
                <h2 className="pv2-h2 mt-2">{cv.who_it_suits.title}</h2>
              </Reveal>
              <ul>
                {cv.who_it_suits.items.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          </Container>
        </Section>
      )}

      {/* Section 6 — WHAT WE WILL NOT TELL YOU (the personality section) */}
      {cv.refuse_list && (
        <Section className="py-14 border-t border-rule/60" data-testid="pv2-refuse">
          <Container>
            <div className="pv2">
              <div className="pv2-refuse">
                <p className="pv2-eyebrow" style={{ color: "var(--c-alert)" }}>Section 6</p>
                <h2 className="pv2-h2 mt-2">{cv.refuse_list.title}</h2>
                <ul>
                  {cv.refuse_list.items.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Container>
        </Section>
      )}

      {/* Section 7 — PROOF. Built from the product's own certificate / X-ray /
          rotation-video fields. Every value describes THIS item. */}
      <Section className="py-14 border-t border-rule/60" data-testid="pv2-proof">
        <Container>
          <div className="pv2">
            <Reveal>
              <p className="pv2-eyebrow">Section 7</p>
              <h2 className="pv2-h2 mt-2">Proof — this item, not a representative example</h2>
            </Reveal>

            {product.certificate ? (
              <div className="mt-8 border border-rule bg-surface p-6">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-verify" aria-hidden="true" />
                  <div>
                    <p className="ui text-[12px] font-medium uppercase tracking-[0.16em] text-verify">Laboratory report</p>
                    <p className="pv2-para mt-1.5 !text-[15px]">
                      {product.certificate.lab_name} · report{" "}
                      <span className="num font-medium text-ink">{product.certificate.report_number}</span>
                      {" "}dated {product.certificate.report_date}.
                    </p>
                    <a
                      href={product.certificate.verification_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-u ui mt-3 inline-flex items-center gap-1.5 text-sm text-verify"
                      data-testid="pv2-verify-link"
                    >
                      Verify on {product.certificate.lab_name.split(" ")[0].toLowerCase()}'s own site
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </div>
            ) : product.type === "rudraksha" && !product.rudraksha?.xray_image_url ? (
              <p className="pv2-para mt-6 text-dim">
                This bead is priced below our ₹3,000 X-ray threshold, so it is described honestly
                rather than radiographed. Beads above the threshold (like this listing's higher-mukhi
                siblings) always show a published X-ray with a chamber count.
              </p>
            ) : null}

            {product.rudraksha?.xray_image_url && (
              <div className="mt-6 border border-rule bg-surface p-6" data-testid="pv2-xray">
                <div className="flex items-start gap-3">
                  <ScanLine className="mt-0.5 h-5 w-5 shrink-0 text-ink" aria-hidden="true" />
                  <div className="w-full">
                    <p className="ui text-[12px] font-medium uppercase tracking-[0.16em] text-ink">
                      X-ray radiograph — {product.rudraksha.chambers_visible} chambers countable
                    </p>
                    <p className="pv2-para mt-1.5 !text-[15px]">
                      Taken {product.rudraksha.xray_date}. External clefts match internal chambers,
                      which is the only proof of count a rudraksha can offer.
                    </p>
                    <img
                      src={product.rudraksha.xray_image_url}
                      alt={`X-ray radiograph of this ${product.rudraksha.mukhi} mukhi rudraksha with ${product.rudraksha.chambers_visible} chambers visible`}
                      className="mt-4 w-full max-w-md border border-rule bg-ink"
                    />
                  </div>
                </div>
              </div>
            )}

            {product.video_url && (
              <div className="mt-6 border border-rule bg-surface p-6">
                <p className="ui text-[12px] font-medium uppercase tracking-[0.16em] text-ink">
                  Rotation video — this exact piece
                </p>
                <p className="pv2-para mt-1.5 !text-[15px]">
                  Filmed under diffuse light, no music, no filter. Included for every gemstone above
                  ₹10,000 and every rudraksha where the surface warrants it.
                </p>
              </div>
            )}

            {cv.voice_note && (
              <p className="pv2-voice mt-10" data-testid="pv2-voice-note">{cv.voice_note}</p>
            )}
          </div>
        </Container>
      </Section>
    </>
  );
}
